import { View } from "@novorender/api";
import { ReadonlyVec2, ReadonlyVec3, vec2 } from "gl-matrix";

import { clamp, isPointInAabb2, lineSegmentIntersectsAabb2 } from "utils/math";

export type LineLabel = {
    readonly position: ReadonlyVec3;
    readonly start: ReadonlyVec3;
    readonly end: ReadonlyVec3;
    position2d?: vec2;
    start2d?: vec2;
    end2d?: vec2;
    dir2d?: vec2;
    dist2d: number;
};

/**
 * Update line label 2D coordinates based on the 3D position and view.
 * Unsets the position if line in 2D is too short, unless `hideWhenShort` is false.
 * @param label LineLabel
 * @param view
 * @param toScreenSpace
 * @param hideWhenShort Whether to hide the label when it doesn't fit on the line. Defaults to true.
 */
export function updateLineLabel2d(
    label: LineLabel,
    view: View,
    toScreenSpace: (v: ReadonlyVec3) => vec2 | undefined,
    { hideWhenShort = true }: { hideWhenShort?: boolean } = {},
) {
    label.position2d = toScreenSpace(label.position);

    const line2d = view.measure?.draw.toScreenSpace([label.start, label.end], { round: false })?.screenPoints;
    [label.start2d, label.end2d] = line2d ?? [undefined, undefined];

    if (label.start2d && label.end2d) {
        label.dist2d = vec2.dist(label.start2d, label.end2d);
        label.dir2d = vec2.sub(vec2.create(), label.end2d, label.start2d);
        vec2.normalize(label.dir2d, label.dir2d);

        if (!label.position2d) {
            label.position2d = vec2.lerp(vec2.create(), label.start2d, label.end2d, 0.5);
        }

        if (hideWhenShort && label.dist2d < calcMinDimensionalDist(label.dir2d, 60, 30)) {
            label.position2d = undefined;
        }
    } else {
        label.start2d = undefined;
        label.position2d = undefined;
        label.end2d = undefined;
        label.dist2d = 0;
        label.dir2d = undefined;
        label.position2d = undefined;
    }
}

/**
 * Check that the labels are within screen bounds.
 * If label is outside - it's put on the edge of the screen.
 * Only if the line is visible itself
 * @param label LabelInfo
 * @param width canvas width
 * @param height canvas height
 */
export function ensureLabelIsOnScreen(label: LineLabel, width: number, height: number, paddingX = 50, paddingY = 20) {
    if (!label.position2d) {
        return;
    }

    if (
        label.start2d &&
        label.end2d &&
        lineSegmentIntersectsAabb2(label.start2d, label.end2d, {
            min: vec2.fromValues(0, 0),
            max: vec2.fromValues(width, height),
        })
    ) {
        let t: number | undefined;
        const x = label.position2d[0];
        if (x < paddingX) {
            t = (paddingX - label.start2d[0]) / (label.end2d[0] - label.start2d[0]);
        } else if (x > width - paddingX) {
            t = (width - paddingX - label.start2d[0]) / (label.end2d[0] - label.start2d[0]);
        }
        if (t !== undefined) {
            vec2.lerp(label.position2d, label.start2d, label.end2d, clamp(t, 0, 1));
            t = undefined;
        }

        const y = label.position2d[1];
        if (y < paddingY) {
            t = (paddingY - label.start2d[1]) / (label.end2d[1] - label.start2d[1]);
        } else if (y > height - paddingY) {
            t = (height - paddingY - label.start2d[1]) / (label.end2d[1] - label.start2d[1]);
        }
        if (t !== undefined) {
            vec2.lerp(label.position2d, label.start2d, label.end2d, clamp(t, 0, 1));
        }
    }
}

/**
 * If pos collides with any of the avoid points, it is moved to the closest point on the line segment l1-l2.
 * Both pos and avoid are lying on the line segment l1-l2.
 * @param pos Position to adjust, mutable
 * @param width Width of the position AABB
 * @param height Height of the position AABB
 * @param lineLabel Line label containing start2d, end2d and dir2d
 * @param avoid Array of points to avoid, lying on the line segment l1-l2
 */
export function avoidOverlapOnLineSegment({
    pos,
    width,
    height,
    lineLabel,
    avoid,
}: {
    pos: vec2;
    width: number;
    height: number;
    lineLabel: LineLabel;
    avoid: ReadonlyVec2[];
}) {
    if (!lineLabel.start2d || !lineLabel.end2d || !lineLabel.dir2d) {
        return;
    }

    const hw = width / 2;
    const hh = height / 2;
    const posAabb = {
        min: vec2.fromValues(pos[0] - hw, pos[1] - hh),
        max: vec2.fromValues(pos[0] + hw, pos[1] + hh),
    };

    // Find closest avoid point
    let closestAvoidIdx = 0;
    let minAvoidSqrDist = Number.MAX_SAFE_INTEGER;
    for (let i = 0; i < avoid.length; i++) {
        const sqrDist = vec2.sqrDist(pos, avoid[i]);
        if (sqrDist < minAvoidSqrDist) {
            minAvoidSqrDist = sqrDist;
            closestAvoidIdx = i;
        }
    }

    const avoidPoint = avoid[closestAvoidIdx];
    // Stop if there's no intersection
    if (!isPointInAabb2(posAabb, avoidPoint, 0)) {
        return;
    }

    // Move pos away from the avoid point along the line in avoid->pos direction
    const avoidToPosDir = vec2.sub(vec2.create(), pos, avoidPoint);
    if (vec2.sqrLen(avoidToPosDir) === 0) {
        const center = vec2.lerp(vec2.create(), lineLabel.start2d, lineLabel.end2d, 0.5);
        vec2.sub(avoidToPosDir, center, avoidPoint);
    }
    vec2.normalize(avoidToPosDir, avoidToPosDir);
    let dir = lineLabel.dir2d;
    vec2.normalize(dir, dir);
    if (vec2.dot(avoidToPosDir, dir) < 0) {
        dir = vec2.negate(vec2.create(), dir);
    }

    // Move pos away from avoid point so that avoid point lies on the border of the pos aabb
    const offset = calcMinDimensionalDist(dir, hw, hh);
    vec2.scaleAndAdd(pos, avoidPoint, dir, offset);

    // Check if it overlaps another point and if it does - place it right between those 2 points.
    // It might overlap with those points, but that's the best we can do without moving pos too far and off the line segment.
    let closestAvoidIdx2 = 0;
    minAvoidSqrDist = Number.MAX_SAFE_INTEGER;
    for (let i = 0; i < avoid.length; i++) {
        const sqrDist = vec2.sqrDist(pos, avoid[i]);
        if (sqrDist < minAvoidSqrDist) {
            minAvoidSqrDist = sqrDist;
            closestAvoidIdx2 = i;
        }
    }

    if (closestAvoidIdx2 === closestAvoidIdx || !isPointInAabb2(posAabb, avoid[closestAvoidIdx2], 0)) {
        // No other point is closer, so we are done
        return;
    }

    vec2.lerp(pos, avoidPoint, avoid[closestAvoidIdx2], 0.5);
}

/**
 * Given min distances in X and Y directions, calculates the minimum length of a line segment in the given direction.
 * More visually:
 * - Imagine rectangle with minDistX=width/2 and minDistY=height/2
 * - Draw line from the center of the rectangle in the given direction
 * - Find intersection of the line with the rectangle
 * - The length of the line segment from the center to the intersection is the result
 * Used to find overlaps between labels to account for differences between width and height.
 * @param dir The direction vector of the line segment.
 * @param minDistX The width of the line segment.
 * @param minDistY The height of the line segment.
 * @returns Direction min distance.
 */
export function calcMinDimensionalDist(dir: ReadonlyVec2, minDistX: number, minDistY: number): number {
    if (dir[0] === 0) {
        return minDistY;
    } else if (dir[1] === 0) {
        return minDistX;
    } else {
        const tx = minDistX / Math.abs(dir[0]);
        const ty = minDistY / Math.abs(dir[1]);
        return Math.min(tx, ty);
    }
}
